/**
 * Minimal Microsoft Authentication Library (MSAL) helper.
 *
 * The Microsoft analogue of `google-gis.ts`. Unlike GIS, MSAL exposes a
 * click-triggered popup flow, so the existing custom "Continue with Microsoft"
 * button can drive it directly. {@link signInWithMicrosoftPopup} resolves with
 * an **ID token** (a JWT) which we POST to `/auth/microsoft`.
 *
 * Set `VITE_MICROSOFT_CLIENT_ID` to an Entra ID (Azure AD) **Web/SPA** app
 * registration's client ID. Optionally set `VITE_MICROSOFT_TENANT_ID` to scope
 * sign-in to one tenant (a tenant ID/domain, or `common` / `organizations`);
 * it defaults to `common`.
 */
import { env } from '@/config/env'

/** The subset of an MSAL `AuthenticationResult` we consume. */
export interface MsalAuthenticationResult {
  /** The ID token (JWT) to send to the backend as `idToken`. */
  idToken: string
  accessToken: string
}

interface MsalConfiguration {
  auth: {
    clientId: string
    authority?: string
    redirectUri?: string
  }
  cache?: { cacheLocation?: 'sessionStorage' | 'localStorage' }
}

interface MsalPopupRequest {
  scopes: Array<string>
  prompt?: 'login' | 'none' | 'consent' | 'select_account'
}

interface MsalPublicClientApplication {
  /** Required in MSAL v3+, absent in v2 — called only when present. */
  initialize?: () => Promise<void>
  loginPopup: (request?: MsalPopupRequest) => Promise<MsalAuthenticationResult>
}

interface MsalNamespace {
  PublicClientApplication: new (config: MsalConfiguration) => MsalPublicClientApplication
}

declare global {
  interface Window {
    msal?: MsalNamespace
  }
}

/** Configured Entra ID client ID (empty string when unset). */
export const MICROSOFT_CLIENT_ID = env.microsoftClientId.trim()

const MICROSOFT_TENANT = env.microsoftTenantId.trim() || 'common'

/** Sign-in authority — `https://login.microsoftonline.com/<tenant>`. */
export const MICROSOFT_AUTHORITY = `https://login.microsoftonline.com/${MICROSOFT_TENANT}`

// Loaded from Microsoft's official CDN (the analogue of Google's gsi/client).
const MSAL_SRC = 'https://alcdn.msauth.net/browser/2.38.2/js/msal-browser.min.js'

const LOGIN_SCOPES = ['openid', 'profile', 'email']

let loader: Promise<void> | null = null

/** Load the MSAL browser script once and resolve when `window.msal` is ready. */
export function loadMsal(): Promise<void> {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('MSAL can only load in the browser'))
  }
  if (window.msal?.PublicClientApplication) return Promise.resolve()
  if (loader) return loader

  loader = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script')
    script.src = MSAL_SRC
    script.async = true
    script.defer = true
    script.onload = () => resolve()
    script.onerror = () => {
      loader = null
      reject(new Error('Failed to load Microsoft Sign-In'))
    }
    document.head.appendChild(script)
  })
  return loader
}

let instance: Promise<MsalPublicClientApplication> | null = null

/** Lazily build (and, on v3+, initialize) the singleton MSAL instance. */
function getMsalInstance(): Promise<MsalPublicClientApplication> {
  if (instance) return instance
  instance = loadMsal().then(() => {
    if (!window.msal) throw new Error('MSAL failed to load')
    const app = new window.msal.PublicClientApplication({
      auth: {
        clientId: MICROSOFT_CLIENT_ID,
        authority: MICROSOFT_AUTHORITY,
        redirectUri: window.location.href,
      },
      cache: { cacheLocation: 'sessionStorage' },
    })
    // MSAL v3+ requires an explicit init; v2 has no such method.
    return app.initialize ? app.initialize().then(() => app) : app
  })
  // Don't cache a failed load — allow a later retry to rebuild the instance.
  instance.catch(() => {
    instance = null
  })
  return instance
}

/**
 * Open the Microsoft sign-in popup and resolve with the ID token (JWT).
 *
 * @throws when MSAL fails to load or the popup errors. A user who closes the
 *   popup yields a `BrowserAuthError` with `errorCode` `user_cancelled` — use
 *   {@link isMicrosoftCancellation} to distinguish that from a real failure.
 */
export async function signInWithMicrosoftPopup(): Promise<string> {
  const app = await getMsalInstance()
  const result = await app.loginPopup({
    scopes: LOGIN_SCOPES,
    prompt: 'select_account',
  })
  return result.idToken
}

/** True when the popup error is the user dismissing/closing the popup. */
export function isMicrosoftCancellation(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    'errorCode' in error &&
    (error as { errorCode?: string }).errorCode === 'user_cancelled'
  )
}
